import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';

export const getAttachmentUrl = (attachment) => {
  if (!attachment || typeof attachment !== 'object') return '';
  return (
    attachment.fileUrl ||
    attachment.file_url ||
    attachment.imageUrl ||
    attachment.image_url ||
    attachment.pdfUrl ||
    attachment.pdf_url ||
    ''
  );
};

export const isImageAttachment = (attachment) => {
  const mime = attachment?.mimeType || attachment?.mime_type || '';
  const name = attachment?.fileName || attachment?.file_name || '';
  return (
    String(mime).startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(String(name))
  );
};

const downloadAttachment = (url, fileName) => {
  if (!url) return;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  if (fileName) {
    anchor.download = fileName;
  }
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export default function AttachmentPreviewPanel({ attachment, compact = false }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const url = getAttachmentUrl(attachment);

  if (!url) return null;

  const fileName = attachment?.fileName || attachment?.file_name || 'Uploaded file';
  const isImage = isImageAttachment(attachment);
  const thumbMaxWidth = compact ? 160 : 200;
  const thumbMaxHeight = compact ? 110 : 140;

  return (
    <Box sx={{ mt: 1.5 }}>
      {isImage ? (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setLightboxOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setLightboxOpen(true);
            }
          }}
          sx={{
            position: 'relative',
            display: 'inline-block',
            cursor: 'zoom-in',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            '&:hover .attachment-enlarge-overlay': {
              opacity: 1,
            },
          }}
        >
          <Box
            component="img"
            src={url}
            alt={fileName}
            sx={{
              display: 'block',
              maxWidth: thumbMaxWidth,
              maxHeight: thumbMaxHeight,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
          <Box
            className="attachment-enlarge-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              color: 'common.white',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            <ZoomInRoundedIcon fontSize="small" />
            Click to enlarge
          </Box>
        </Box>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            p: 1.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            maxWidth: 360,
          }}
        >
          <PictureAsPdfOutlinedIcon color="error" />
          <Typography variant="body2" noWrap title={fileName}>
            {fileName}
          </Typography>
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewRoundedIcon />}
          onClick={() => {
            if (isImage) {
              setLightboxOpen(true);
            } else {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {isImage ? 'View Full Size' : 'Preview PDF'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadRoundedIcon />}
          onClick={() => downloadAttachment(url, fileName)}
        >
          Download
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {fileName}
      </Typography>

      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'grey.900',
            backgroundImage: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="subtitle2" color="common.white" noWrap sx={{ pr: 2 }}>
            {fileName}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => downloadAttachment(url, fileName)}
              sx={{ color: 'common.white' }}
              aria-label="Download image"
            >
              <DownloadRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setLightboxOpen(false)}
              sx={{ color: 'common.white' }}
              aria-label="Close preview"
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <DialogContent
          sx={{
            p: { xs: 1, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 240, sm: 360 },
          }}
        >
          <Box
            component="img"
            src={url}
            alt={fileName}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: '70vh', sm: '80vh' },
              objectFit: 'contain',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
